import { type RefObject, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error-message'
import {
  dataGridSavedViewsApi,
  type DataGridSavedView,
  type DataGridSavedViewState,
} from '../api/data-grid-saved-views-api'
import { canonicalizeDataGridFilters } from './url-state'
import { sameDataGridTable, type DataGridTable } from './table-state'
import type { DataGridFilter } from './types'

const SAVE_ERROR_CODES = {
  DATA_GRID_SAVED_VIEW_NAME_EXISTS: 'A saved view with this name already exists.',
  DATA_GRID_SAVED_VIEW_FILTERS_EXIST: 'This table is already saved as a view.',
  DATA_GRID_SAVED_VIEW_LIMIT_REACHED: 'You have reached the saved-view limit.',
} as const

const VIEW_GONE_ERROR_CODES = {
  DATA_GRID_SAVED_VIEW_NOT_FOUND: 'This view no longer exists.',
} as const

const OVERWRITE_ERROR_CODES = {
  DATA_GRID_SAVED_VIEW_FILTERS_EXIST: 'This table is already saved as another view.',
  DATA_GRID_SAVED_VIEW_INVALID: 'The current table can’t be saved as a view.',
  ...VIEW_GONE_ERROR_CODES,
} as const

const RENAME_ERROR_CODES = {
  DATA_GRID_SAVED_VIEW_NAME_EXISTS: SAVE_ERROR_CODES.DATA_GRID_SAVED_VIEW_NAME_EXISTS,
  ...VIEW_GONE_ERROR_CODES,
} as const

const EVERYTHING_MATCHED = 'everything'

type Applying = { from: string; to: string }

type Origin = {
  scopeKey: string
  entryKey: string
  matchedKey: string | null
  viewId: string | null
  applying: Applying | null
}

type Landing = { entryKey: string; phase: 'applying' | 'landed' | 'done' }

export type DataGridSavedViewsOptions = false | {
  nameMaxLength?: number
  createErrorMessage?: (error: unknown) => string
  /**
   * Opens a URL that carries none of the grid's state on the user's default view, replacing the URL with the view's
   * state before any rows load. A URL with any grid state is left as it is.
   */
  landOnDefault?: boolean
}

type MutationState<Input> = {
  isPending: boolean
  isError: boolean
  error: Error | null
  reset: () => void
  mutate: (input: Input, options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) => void
}

export type DataGridSavedViewsState = {
  scopeKey: string
  views: DataGridSavedView[] | undefined
  query: {
    data: DataGridSavedView[] | undefined
    isPending: boolean
    isError: boolean
    isSuccess: boolean
    refetch: () => Promise<unknown>
  }
  activeView: DataGridSavedView | null
  status: 'saved' | 'everything' | 'custom' | 'loading' | 'unavailable'
  /** False at the grid's default table and while the search is still settling. */
  canSave: boolean
  nameMaxLength: number
  /** Applies a saved view, or resets the table to the grid defaults for Everything when given null. */
  apply: (view: DataGridSavedView | null) => void
  /**
   * The view the table was last set to or matched. It stays while the table changes, and clears when the table reads
   * as Everything, the view is deleted, or the user or grid changes. It travels with the browser history entry, so it
   * returns after Back or a reload, but a shared link carries only the table. A grid without URL state keeps it
   * only while mounted.
   */
  originView: DataGridSavedView | null
  /** True from `apply` until the table shows the applied view, while the router commits its search. */
  isApplying: boolean
  /** True while a URL without grid state waits for the views to load or for the default view to apply. */
  awaitingDefault: boolean
  /**
   * True when the current table can be saved over `originView`: the search has settled, no view is being applied,
   * the table differs from the grid defaults, and it is not already that whole-table view. A matched filters-only
   * view qualifies, and saving over it makes it a whole-table view.
   */
  canOverwrite: boolean
  create: MutationState<{ name: string }> & {
    errorMessage: (error: unknown) => string
  }
  rename: MutationState<{ savedViewId: string; name: string }> & {
    errorMessage: (error: unknown) => string
  }
  /** Marks a view as the user's default for the grid, taking it off any other view, or unmarks it. */
  setDefault: MutationState<{ savedViewId: string; isDefault: boolean }> & {
    errorMessage: (error: unknown) => string
  }
  /** Saves the current table over a view, which keeps its ID, name, and default. */
  overwrite: MutationState<{ savedViewId: string }> & {
    errorMessage: (error: unknown) => string
  }
  delete: MutationState<string>
}

export function useDataGridSavedViews({
  gridId,
  userId,
  table,
  defaultTable,
  everythingTable,
  viewTable,
  settled,
  apply,
  restoredOriginId,
  originRef,
  entry,
  options,
}: {
  gridId: string
  userId: string | undefined
  table: DataGridTable
  defaultTable: DataGridTable
  /** The table that reads as Everything: the grid defaults, with the remembered columns when the URL names none. */
  everythingTable: DataGridTable
  /** The table a saved view shows once applied, or null when the grid no longer accepts its filters. */
  viewTable: (filters: readonly DataGridFilter[], state: DataGridSavedViewState) => DataGridTable | null
  settled: boolean
  /**
   * Writes a view's table, or the grid defaults for null, to the URL, or to the grid's own state when URL state is
   * off; settles once the router shows it, or once written. A landing applies the view to the page's starting table
   * instead of the one currently shown.
   */
  apply: (view: DataGridSavedView | null, options?: { landing?: boolean }) => Promise<void>
  /** The view the history entry recorded the table coming from, if any. */
  restoredOriginId: string | null
  /** Kept at the current origin's ID so table changes can record it with the history entry. */
  originRef: RefObject<string | null>
  /**
   * The history entry the page is on, one per mount without URL state, and whether it carries none of the grid's
   * state.
   */
  entry: { key: string; bare: boolean }
  options: DataGridSavedViewsOptions | undefined
}): DataGridSavedViewsState | undefined {
  const queryClient = useQueryClient()
  const enabled = options !== false && userId !== undefined
  const queryKey = ['data-grid', 'saved-views', userId ?? '', gridId] as const
  const scopeKey = JSON.stringify([userId, gridId])
  const currentScopeRef = useRef(scopeKey)
  useEffect(() => { currentScopeRef.current = scopeKey }, [scopeKey])
  const viewsQuery = useQuery({
    queryKey,
    queryFn: () => dataGridSavedViewsApi.list(gridId),
    enabled,
  })
  const createMutation = useMutation({
    mutationFn: async (input: {
      name: string
      filters: DataGridFilter[]
      state: DataGridSavedViewState
      gridId: string
      scopeKey: string
      queryKey: readonly unknown[]
    }) => ({
      view: await dataGridSavedViewsApi.create(input.gridId, {
        name: input.name,
        filters: input.filters,
        state: input.state,
      }),
      input,
    }),
    onSuccess: async ({ view, input }) => {
      queryClient.setQueryData<DataGridSavedView[]>(input.queryKey, (current) =>
        current === undefined ? undefined : [...current, view]
          .sort((left, right) => left.name.localeCompare(right.name)))
      await queryClient.invalidateQueries({ queryKey: input.queryKey })
    },
  })
  const renameMutation = useMutation({
    mutationFn: async (input: {
      savedViewId: string
      name: string
      gridId: string
      scopeKey: string
      queryKey: readonly unknown[]
    }) => {
      await dataGridSavedViewsApi.update(input.gridId, input.savedViewId, { name: input.name })
      return input
    },
    onSuccess: ({ savedViewId, name, queryKey: originQueryKey }) => {
      queryClient.setQueryData<DataGridSavedView[]>(originQueryKey, (current) => current
        ?.map((view) => view.savedViewId === savedViewId ? { ...view, name } : view)
        .sort((left, right) => left.name.localeCompare(right.name)))
    },
    onSettled: (_result, _error, input) => queryClient.invalidateQueries({ queryKey: input.queryKey }),
  })
  const defaultMutation = useMutation({
    mutationFn: async (input: {
      savedViewId: string
      isDefault: boolean
      gridId: string
      scopeKey: string
      queryKey: readonly unknown[]
    }) => {
      await dataGridSavedViewsApi.update(input.gridId, input.savedViewId, { isDefault: input.isDefault })
      return input
    },
    onSuccess: ({ savedViewId, isDefault, queryKey: originQueryKey }) => {
      queryClient.setQueryData<DataGridSavedView[]>(originQueryKey, (current) => current?.map((view) => ({
        ...view,
        isDefault: view.savedViewId === savedViewId ? isDefault : view.isDefault && !isDefault,
      })))
    },
    onSettled: (_result, _error, input) => queryClient.invalidateQueries({ queryKey: input.queryKey }),
  })
  const [origin, setOrigin] = useState<Origin>(() => restoredOrigin(scopeKey, entry.key, null, restoredOriginId))
  useEffect(() => { originRef.current = origin.viewId }, [origin.viewId, originRef])
  const overwriteMutation = useMutation({
    mutationFn: (input: {
      savedViewId: string
      filters: DataGridFilter[]
      state: DataGridSavedViewState
      gridId: string
      scopeKey: string
      queryKey: readonly unknown[]
    }) => dataGridSavedViewsApi.overwrite(input.gridId, input.savedViewId, {
      filters: input.filters,
      state: input.state,
    }),
    onSuccess: (saved, { queryKey: originQueryKey }) => {
      queryClient.setQueryData<DataGridSavedView[]>(originQueryKey, (current) =>
        current?.map((view) => view.savedViewId === saved.savedViewId ? saved : view))
    },
    onSettled: (_result, _error, input) => queryClient.invalidateQueries({ queryKey: input.queryKey }),
  })
  const deleteMutation = useMutation({
    mutationFn: async (input: {
      savedViewId: string
      gridId: string
      scopeKey: string
      queryKey: readonly unknown[]
    }) => {
      try {
        await dataGridSavedViewsApi.delete(input.gridId, input.savedViewId)
      } catch (error) {
        if (!viewAlreadyGone(error)) throw error
      }
      return input
    },
    onSuccess: ({ savedViewId, queryKey: originQueryKey }) => {
      queryClient.setQueryData<DataGridSavedView[]>(originQueryKey, (current) =>
        current?.filter((view) => view.savedViewId !== savedViewId))
    },
    onSettled: (_result, _error, input) => queryClient.invalidateQueries({ queryKey: input.queryKey }),
  })
  const [landing, setLanding] = useState<Landing | null>(null)
  const landedEntryRef = useRef<string | null>(null)
  const landOnDefault = enabled && options !== undefined && options.landOnDefault === true
  const defaultView = viewsQuery.data?.find((view) => view.isDefault) ?? null
  const listSettled = viewsQuery.isSuccess || viewsQuery.isError
  const entryLanding = landing?.entryKey === entry.key ? landing : null
  if (landOnDefault && entryLanding === null && (!entry.bare || (listSettled && defaultView === null))) {
    setLanding({ entryKey: entry.key, phase: 'done' })
  }
  if (entryLanding?.phase === 'landed' && settled) setLanding({ entryKey: entry.key, phase: 'done' })
  const landingTarget = landOnDefault && entry.bare && entryLanding === null && listSettled ? defaultView : null
  const awaitingDefault = landOnDefault && (entryLanding === null
    ? entry.bare && (!listSettled || defaultView !== null)
    : entryLanding.phase !== 'done')
  useEffect(() => {
    if (landedEntryRef.current !== entry.key) landedEntryRef.current = null
  }, [entry.key])
  useEffect(() => {
    if (landingTarget === null || landedEntryRef.current === entry.key) return
    const entryKey = entry.key
    landedEntryRef.current = entryKey
    setLanding({ entryKey, phase: 'applying' })
    const finish = () => setLanding((current) => current?.entryKey === entryKey ? { entryKey, phase: 'landed' } : current)
    apply(landingTarget, { landing: true }).then(finish, finish)
  }, [apply, entry.key, landingTarget])
  if (!enabled) return undefined

  const canonicalFilters = canonicalizeDataGridFilters(table.filters)
  const filterIdentity = JSON.stringify(canonicalFilters)
  const currentTable = { ...table, filters: canonicalFilters }
  const views = viewsQuery.data
  const activeView = views
    ? views.find((view) => {
        const viewed = view.state === null ? null : viewTable(view.filters, view.state)
        return viewed !== null
          && !sameDataGridTable(viewed, defaultTable)
          && sameDataGridTable(currentTable, viewed)
      })
      ?? (canonicalFilters.length
        ? views.find((view) => view.state === null && JSON.stringify(view.filters) === filterIdentity)
        : undefined)
      ?? null
    : null
  const status = activeView
    ? 'saved'
    : sameDataGridTable(currentTable, everythingTable)
      ? 'everything'
      : viewsQuery.data !== undefined
        ? 'custom'
        : viewsQuery.isPending ? 'loading' : 'unavailable'
  const canSave = settled && !sameDataGridTable(currentTable, defaultTable)
  const tableIdentity = JSON.stringify(currentTable)
  const matchedKey = activeView?.savedViewId ?? (status === 'everything' ? EVERYTHING_MATCHED : null)
  const tracked = origin.scopeKey === scopeKey && origin.entryKey === entry.key
    ? origin
    : restoredOrigin(scopeKey, entry.key, matchedKey, restoredOriginId)
  const originId = nextOriginId(tracked, matchedKey)
  const applying = tracked.applying?.from === tableIdentity && tracked.applying.to !== matchedKey
    ? tracked.applying
    : null
  if (tracked !== origin || matchedKey !== tracked.matchedKey || applying !== tracked.applying) {
    setOrigin({ scopeKey, entryKey: entry.key, matchedKey, viewId: originId, applying })
  }
  const originView = views?.find((view) => view.savedViewId === originId) ?? null
  const isApplying = applying !== null
  const canOverwrite = canSave && originView !== null && !activeView?.state && !isApplying
  const currentState: DataGridSavedViewState = {
    columns: table.columns,
    sort: table.sort,
    ...(table.search ? { search: table.search } : {}),
    parameters: table.parameters,
  }
  const whileInScope = (mutationOptions?: { onSuccess?: () => void; onError?: (error: unknown) => void }) => ({
    onSuccess: () => {
      if (currentScopeRef.current === scopeKey) mutationOptions?.onSuccess?.()
    },
    onError: (error: unknown) => {
      if (currentScopeRef.current === scopeKey) mutationOptions?.onError?.(error)
    },
  })
  const createErrorMessage = options && options.createErrorMessage
    ? options.createErrorMessage
    : (error: unknown) => apiErrorMessage(
        error,
        'Couldn’t save this view. Try again.',
        { codes: SAVE_ERROR_CODES },
      )

  return {
    scopeKey,
    views,
    query: {
      data: viewsQuery.data,
      isPending: viewsQuery.isPending,
      isError: viewsQuery.isError,
      isSuccess: viewsQuery.isSuccess,
      refetch: viewsQuery.refetch,
    },
    activeView,
    status,
    canSave,
    nameMaxLength: options && options.nameMaxLength ? options.nameMaxLength : 200,
    apply: (view) => {
      setOrigin((current) => ({
        ...current,
        viewId: view?.savedViewId ?? null,
        applying: { from: tableIdentity, to: view?.savedViewId ?? EVERYTHING_MATCHED },
      }))
      void apply(view)
    },
    originView,
    isApplying,
    awaitingDefault,
    canOverwrite,
    create: {
      ...stateInScope(createMutation, scopeKey),
      errorMessage: createErrorMessage,
      mutate: ({ name }, mutationOptions) => createMutation.mutate({
        name,
        filters: canonicalFilters,
        state: currentState,
        gridId,
        scopeKey,
        queryKey,
      }, whileInScope(mutationOptions)),
    },
    rename: {
      ...stateInScope(renameMutation, scopeKey),
      errorMessage: (error) => apiErrorMessage(
        error,
        'Couldn’t rename this view. Try again.',
        { codes: RENAME_ERROR_CODES },
      ),
      mutate: ({ savedViewId, name }, mutationOptions) => renameMutation.mutate({
        savedViewId,
        name,
        gridId,
        scopeKey,
        queryKey,
      }, whileInScope(mutationOptions)),
    },
    setDefault: {
      ...stateInScope(defaultMutation, scopeKey),
      errorMessage: (error) => apiErrorMessage(
        error,
        'Couldn’t change the default view. Try again.',
        { codes: VIEW_GONE_ERROR_CODES },
      ),
      mutate: ({ savedViewId, isDefault }, mutationOptions) => defaultMutation.mutate({
        savedViewId,
        isDefault,
        gridId,
        scopeKey,
        queryKey,
      }, whileInScope(mutationOptions)),
    },
    overwrite: {
      ...stateInScope(overwriteMutation, scopeKey),
      errorMessage: (error) => apiErrorMessage(
        error,
        'Couldn’t save over this view. Try again.',
        { codes: OVERWRITE_ERROR_CODES },
      ),
      mutate: ({ savedViewId }, mutationOptions) => overwriteMutation.mutate({
        savedViewId,
        filters: canonicalFilters,
        state: currentState,
        gridId,
        scopeKey,
        queryKey,
      }, whileInScope(mutationOptions)),
    },
    delete: {
      ...stateInScope(deleteMutation, scopeKey),
      mutate: (savedViewId, mutationOptions) => deleteMutation.mutate({
        savedViewId,
        gridId,
        scopeKey,
        queryKey,
      }, whileInScope(mutationOptions)),
    },
  }
}

function viewAlreadyGone(error: unknown): boolean {
  return error instanceof ApiError
    && error.code !== undefined
    && Object.hasOwn(VIEW_GONE_ERROR_CODES, error.code)
}

/**
 * The origin a history entry recorded. The router moves to a new entry before the table's search follows, so the
 * table the page still shows counts as already matched rather than as a change that would replace the recorded view.
 */
function restoredOrigin(scopeKey: string, entryKey: string, matchedKey: string | null, viewId: string | null): Origin {
  return { scopeKey, entryKey, matchedKey, viewId, applying: null }
}

function nextOriginId(tracked: Origin, matchedKey: string | null) {
  if (matchedKey === tracked.matchedKey) return tracked.viewId
  if (matchedKey === EVERYTHING_MATCHED) return null
  return matchedKey ?? tracked.viewId
}

function stateInScope(
  mutation: {
    variables: { scopeKey: string } | undefined
    isPending: boolean
    isError: boolean
    error: Error | null
    reset: () => void
  },
  scopeKey: string,
) {
  const inScope = mutation.variables === undefined || mutation.variables.scopeKey === scopeKey
  return {
    isPending: inScope && mutation.isPending,
    isError: inScope && mutation.isError,
    error: inScope ? mutation.error : null,
    reset: mutation.reset,
  }
}
